import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { getKvkkRequests } from '../../../services/kvkkService';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';
import { formatDateTime } from '../../../utils/Utils';
import { csvFilterChip } from '../../../utils/csvFilterChip';
import { StatusBadge } from '../../../components/kvkk/statusBadge';

// İK — KVKK Talepleri listesi. SUNUCU taraflı: sayfa/sıra/süzgeç backend'de
// (KvkkDataRequestController.GetAll). Kolon adı == sunucu parametresi (parseTableParams
// `column.name` + `[op]` üretir): employeeName[like], type[in], status[in],
// createDate[between|gte|lte]; orderBy kolon adıdır, backend küçük harfe indirip
// employeename|status|type eşler, diğerleri CreateDate'e düşer. Backend'in
// sıralayamadığı kolonlar (handledByName, closedAt) `sort: false` — sahte ok işareti yok.
export const TYPE_OPTIONS = ['deletion', 'correction', 'access'];
export const STATUS_OPTIONS = ['open', 'in_review', 'completed', 'rejected'];

// kvkk-consents/config ile aynı CSV çoklu seçim süzgeci (değer "a,b" → status[in]=a,b).
function csvMultiFilter(label, options, optionLabel) {
  return {
    defaultOp: 'in',
    display: (filterList, onChange, index, column) => {
      const current = filterList[index] && filterList[index][0]
        ? filterList[index][0].split(',').filter(Boolean)
        : [];
      return (
        <FormControl variant="standard" fullWidth>
          <InputLabel>{label}</InputLabel>
          <Select
            multiple
            value={current}
            onChange={(e) => {
              const v = e.target.value;
              const csv = Array.isArray(v) ? v.filter(Boolean).join(',') : '';
              filterList[index] = csv ? [csv] : [];
              onChange(filterList[index], index, column);
            }}
            renderValue={(sel) => sel.map((s) => optionLabel(s)).join(', ')}
          >
            {options.map((o) => (
              <MenuItem key={o} value={o}>{optionLabel(o)}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    },
  };
}

const useKvkkRequestsApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  // api.js başarısız GET'i reddetmez (EMPTY_RESPONSE + interceptor toast'u); sinyal
  // `res.data.status`. "Yüklenemedi" boş listeden AYRI gösterilir.
  const [loadFailed, setLoadFailed] = useState(false);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    let cancelled = false;
    setFetching(true);
    getKvkkRequests(new URLSearchParams(paramsToUse).toString())
      .then((res) => {
        if (cancelled) return;
        const body = res?.data;
        if (!body?.status) {
          setLoadFailed(true);
          setData([]);
          setPagination((p) => ({ ...p, totalRowCount: 0 }));
          return;
        }
        const result = body.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setLoadFailed(false);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount ?? items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || Number(paramsToUse.pageSize) || 10,
        });
      })
      .finally(() => { if (!cancelled) setFetching(false); });
    return () => { cancelled = true; };
  }, [queryParams, refreshTrigger]);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  const typeLabel = useCallback((v) => t(`kvkkRequest.type.${v}`), [t]);
  const statusLabel = useCallback((v) => t(`kvkkRequest.status.${v}`), [t]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded', download: false, print: false } },
    {
      name: 'employeeName',
      label: t('kvkkRequest.employee'),
      options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } },
    },
    {
      name: 'type',
      label: t('kvkkRequest.type.label'),
      options: {
        filter: true,
        sort: true,
        filterType: 'custom',
        customBodyRender: (v) => typeLabel(v),
        customFilterListOptions: csvFilterChip(t('kvkkRequest.type.label'), typeLabel),
        filterOptions: csvMultiFilter(t('kvkkRequest.type.label'), TYPE_OPTIONS, typeLabel),
      },
    },
    {
      name: 'status',
      label: t('kvkkRequest.status.label'),
      options: {
        filter: true,
        sort: true,
        filterType: 'custom',
        customBodyRender: (v) => <StatusBadge $status={v}>{statusLabel(v)}</StatusBadge>,
        customFilterListOptions: csvFilterChip(t('kvkkRequest.status.label'), statusLabel),
        filterOptions: csvMultiFilter(t('kvkkRequest.status.label'), STATUS_OPTIONS, statusLabel),
      },
    },
    dateFilterColumn('createDate', t('kvkkRequest.createdAt'), lang, { includeTime: true }),
    {
      name: 'handledByName',
      label: t('kvkkRequest.handledBy'),
      options: { filter: false, sort: false, customBodyRender: (v) => v || '-' },
    },
    {
      name: 'closedAt',
      label: t('kvkkRequest.closedAt'),
      options: { filter: false, sort: false, customBodyRender: (v) => (v ? formatDateTime(v, lang) : '-') },
    },
  ], [t, lang, typeLabel, statusLabel]);

  return { fetching, data, loadFailed, pagination, columns, refresh, statusLabel };
};

export default useKvkkRequestsApi;
